import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Building2, Fingerprint, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import Collection from '@model-core/core/Collection';
import { Avatar } from '@/components/Avatar';
import { AuditDate } from '@/components/AuditDate';
import { Box, StatusBadge, Value } from '@/components/dashboard/DashboardParts';
import { DetailHeader } from '@/components/detail/DetailHeader';
import { DetailPageStatus } from '@/components/detail/DetailPageStatus';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/dialog/ConfirmDialog';
import { getApiErrorMessage } from '@/lib/apiError';
import IamIdentityModel from '@/models/entities/IamIdentityModel';
import MemberModel from '@/models/entities/MemberModel';
import UserModel from '@/models/entities/UserModel';
import UserStatusHistoryModel from '@/models/services/UserStatusHistoryModel';
import { useLoader } from '@/hooks/useLoader';
import { useIsCurrentUser } from '@/security/useIsCurrentUser';
import { UserFormDialog } from './UserFormDialog';
import { getManualChangeBlocker, toggleUserStatus } from './userStatus';
import { loadReferences, resolveReference } from '@/stores/useReferenceOptions';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { COMPANY_ROLE } from '@/models/references/references';
import { AddToCompanyDialog } from '@/components/members/AddToCompanyDialog';
import { PagedStatusHistory } from '@/components/history/PagedStatusHistory';
import { useDataVersion } from '@/hooks/useDataVersion';
import { appRoleIcon, companyRoleIcon } from '@/components/roles/roleIcons';
import { RoleLabel } from '@/components/roles/RoleLabel';

interface UserDashboard {
  user: UserModel;
  members: MemberModel[];
  identities: IamIdentityModel[];
  /** Sociétés que l'utilisateur connecté dirige : fiche employé et ajout d'un employé, droits du Dirigeant */
  directedCompanyIds: Set<number>;
}

const BACK = { to: '/users', label: 'Tous les utilisateurs' };

/** Sociétés que l'utilisateur connecté dirige (GET /members?user=…&companyRole=…) */
async function loadDirectedCompanyIds(): Promise<Set<number>> {
  const currentUserId = useAuthStore.getState().user?.urn.split(':').pop();
  if (!currentUserId) return new Set();
  const director = await resolveReference('companyRoles', COMPANY_ROLE.DIRECTOR);
  const { collection } = await new Collection(MemberModel)
    .setFilter('user', currentUserId)
    .setFilter('companyRole', String(director.id))
    .list();
  return new Set(collection.models.map((member) => member.companyId));
}

async function loadDashboard(id: string): Promise<UserDashboard> {
  const [{ model: user }, members, identities, directedCompanyIds] = await Promise.all([
    new UserModel({ id }).fetch({ silentError: true }),
    new Collection(MemberModel).setFilter('user', id).setSort('company.name').list(),
    new Collection(IamIdentityModel).setFilter('user', id).setSort('userProvider.name').list(),
    // Sans elles (erreur), ni lien ni ajout : la fiche reste lisible
    loadDirectedCompanyIds().catch(() => new Set<number>()),
  ]);

  // Nom des sociétés et des fournisseurs d'identité
  await Promise.all([
    members.collection.fetchResolvable('company'),
    identities.collection.fetchResolvable('userProvider'),
    // Rôle, statuts et rôles dans les sociétés, lus par leur id
    loadReferences('appRoles', 'userStatuses', 'companyRoles'),
  ]);

  return {
    user,
    members: members.collection.models,
    identities: identities.collection.models,
    directedCompanyIds,
  };
}

/**
 * Fiche d'un utilisateur (/users/{id}, admin), comme les dashboards des backoffices de la plateforme :
 * en-tête (informations et actions) puis ses sociétés (gérées par leurs Dirigeants : celles que l'on dirige ouvrent sa fiche
 * employé, et l'on peut l'ajouter à l'une d'elles), ses identités IAM
 * et l'historique de son statut.
 */
export function UserDashboardPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const isCurrentUser = useIsCurrentUser();
  // Rechargée après modification ou changement de statut
  const { data: dashboard, error, loaded, reload } = useLoader(id, loadDashboard);
  // Historique rechargé avec la fiche (après une activation, une désactivation)
  const historyVersion = useDataVersion(dashboard);
  const [dialog, setDialog] = useState<'edit' | 'delete' | 'addCompany' | null>(null);
  const myCompanies = useCompanyStore((state) => state.companies);
  const selectCompany = useCompanyStore((state) => state.select);

  if (!dashboard) {
    return <DetailPageStatus back={BACK} loaded={loaded} error={error} notFound="Utilisateur introuvable." />;
  }

  const { user } = dashboard;
  const isMe = isCurrentUser(user.id);
  // Lié à un IAM : ses identités, chargées avec la fiche
  const iamManaged = dashboard.identities.length > 0;
  const statusBlocker = getManualChangeBlocker(iamManaged, isMe, 'status');
  const deleteBlocker = getManualChangeBlocker(iamManaged, isMe, 'delete');
  // Sociétés où l'ajouter : dirigées par l'utilisateur connecté, dont il n'est pas encore employé
  const addableCompanies = myCompanies.filter(
    (company) =>
      dashboard.directedCompanyIds.has(company.id) &&
      !dashboard.members.some((member) => member.companyId === company.id),
  );

  const toggleStatus = async () => {
    try {
      toast.success(await toggleUserStatus(user));
      reload();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <DetailHeader
        back={BACK}
        label="Utilisateur"
        avatar={<Avatar firstName={user.firstName} lastName={user.lastName} size="lg" tone="accent" />}
        title={
          <>
            {user.fullName}
            {isMe && <span className="ml-2 text-sm font-normal text-slate-400">(vous)</span>}
          </>
        }
        subtitle={<StatusBadge enabled={user.isEnabled} />}
        actions={
          <>
            <Button variant="outline" onClick={() => setDialog('edit')}>
              <Pencil className="w-4 h-4" />
              Modifier
            </Button>
            <Button
              variant="outline"
              onClick={toggleStatus}
              disabled={!!statusBlocker}
              title={statusBlocker ?? undefined}
            >
              <Power className="w-4 h-4" />
              {user.isEnabled ? 'Désactiver' : 'Réactiver'}
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDialog('delete')}
              disabled={!!deleteBlocker}
              title={deleteBlocker ?? undefined}
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </Button>
          </>
        }
      >
        <Value label="Email">{user.email}</Value>
        <Value label="Rôle dans l'application">
          <RoleLabel icon={appRoleIcon(user.appRoleReference)} label={user.appRoleLabel} />
        </Value>
        <Value label="Mot de passe local">{user.passwordDefined ? 'Défini' : 'Non défini'}</Value>
        <Value label="Créé le">
          <AuditDate date={user.createdAt} by={user.createdBy} />
        </Value>
        <Value label="Date de dernière modification">
          <AuditDate date={user.updatedAt} by={user.updatedBy} />
        </Value>
      </DetailHeader>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Les employés d'une société sont gérés par ses Dirigeants (écran Employés) : lien et ajout pour celles qu'on dirige */}
        <Box
          title="Sociétés"
          icon={Building2}
          empty={dashboard.members.length === 0 && 'Rattaché à aucune société.'}
          action={
            addableCompanies.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setDialog('addCompany')}>
                <Plus className="w-4 h-4" />
                Ajouter à une société
              </Button>
            )
          }
        >
          <table className="w-full text-left text-sm" data-testid="user-companies">
            <thead>
              <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="py-2">Société</th>
                <th className="py-2">Rôle</th>
                <th className="py-2">Ajouté le</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dashboard.members.map((member) => (
                <tr key={member.id}>
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold flex-shrink-0">
                        {member.companyName.charAt(0)}
                      </div>
                      {/* Fiche employé : page du Dirigeant de la société, sélectionnée au passage dans la sidebar */}
                      {dashboard.directedCompanyIds.has(member.companyId) ? (
                        <Link
                          to={`/employees/${member.id}`}
                          onClick={() => void selectCompany(member.companyId)}
                          className="font-medium text-slate-900 hover:text-blue-700 hover:underline"
                        >
                          {member.companyName}
                        </Link>
                      ) : (
                        <span className="font-medium text-slate-900">{member.companyName}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3">
                    <RoleLabel
                      icon={companyRoleIcon(member.companyRoleReference)}
                      label={member.companyRoleLabel}
                      className="gap-2 text-slate-700"
                    />
                  </td>
                  <td className="py-3 text-slate-600">
                    <AuditDate date={member.createdAt} by={member.createdBy} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500">
            Les employés d'une société sont gérés par ses Dirigeants (Paramétrage Dirigeant &gt; Employés) : le nom
            d'une société que vous dirigez ouvre sa fiche employé.
          </p>
        </Box>

        <Box
          title="Identités IAM"
          icon={Fingerprint}
          empty={dashboard.identities.length === 0 && 'Aucune : utilisateur géré manuellement.'}
        >
          <ul className="divide-y divide-slate-100" data-testid="user-identities">
            {dashboard.identities.map((identity) => (
              <li key={identity.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-900">{identity.userProviderName}</p>
                  <p className="text-xs text-slate-500">{identity.subjectId}</p>
                </div>
                <StatusBadge enabled={identity.active} />
              </li>
            ))}
          </ul>
        </Box>

        <PagedStatusHistory
          modelClass={UserStatusHistoryModel}
          filters={{ user: Number(user.id) }}
          version={historyVersion}
          className="xl:col-span-2"
          empty="Aucun changement de statut."
          testId="user-status-history"
          toLine={(entry) => ({
            id: entry.id,
            createdAt: entry.createdAt,
            createdBy: entry.createdBy,
            status: <StatusBadge enabled={entry.isEnabled} label={entry.userStatusLabel} />,
          })}
        />
      </div>

      {dialog === 'edit' && (
        <UserFormDialog
          user={user}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            toast.success(`${saved.fullName} a été modifié.`);
            setDialog(null);
            reload();
          }}
        />
      )}

      {dialog === 'addCompany' && (
        <AddToCompanyDialog
          user={user}
          companies={addableCompanies}
          onClose={() => setDialog(null)}
          onSaved={(companyName, teamError) => {
            toast.success(`${user.fullName} a été ajouté à ${companyName}.`);
            if (teamError) toast.error(`Mais pas à l'équipe : ${teamError}`);
            setDialog(null);
            reload();
          }}
        />
      )}

      {dialog === 'delete' && (
        <DeleteUserDialog
          user={user}
          onClose={() => setDialog(null)}
          onDeleted={() => {
            toast.success(`${user.fullName} a été supprimé.`);
            navigate('/users');
          }}
        />
      )}
    </div>
  );
}

/** Confirmation de suppression (DELETE /users/{id}) : ses rattachements aux sociétés partent avec lui */
function DeleteUserDialog({
  user,
  onClose,
  onDeleted,
}: {
  user: UserModel;
  onClose: () => void;
  onDeleted: () => void;
}) {
  return (
    <ConfirmDialog
      title={`Supprimer ${user.fullName} ?`}
      description="Ses rattachements aux sociétés et l'historique de son statut sont aussi supprimés, et ses sessions sont coupées. Cette action est définitive."
      confirmLabel="Supprimer"
      confirmIcon={Trash2}
      destructive
      onConfirm={() => new UserModel({ id: user.id }).destroy()}
      onDone={onDeleted}
      onClose={onClose}
    />
  );
}
